/** @jsxImportSource ../../i18n */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../../theme/cx';

const SIDES = {
  top: 'bottom-full left-1/2 mb-2 -translate-x-1/2',
  bottom: 'top-full left-1/2 mt-2 -translate-x-1/2',
  right: 'left-full top-1/2 ml-2 -translate-y-1/2',
  left: 'right-full top-1/2 mr-2 -translate-y-1/2',
  // Right-aligned with the trigger, for controls against the right edge.
  'top-end': 'bottom-full right-0 mb-2',
  'bottom-end': 'top-full right-0 mt-2',
};

export const Tooltip = ({ content, side = 'top', children, className }) => {
  if (!content) return children;
  return (
    <span className={cx('group/tooltip relative inline-flex', className)}>
      {children}
      <span
        aria-hidden="true"
        className={cx(
          'pointer-events-none absolute z-30 whitespace-nowrap rounded-md border border-line-strong bg-surface-3 px-2 py-1 text-xs font-medium text-fg opacity-0 shadow-pop transition-opacity duration-150',
          'group-hover/tooltip:opacity-100 group-hover/tooltip:delay-300 group-focus-within/tooltip:opacity-100',
          SIDES[side] || SIDES.top,
        )}
      >
        {content}
      </span>
    </span>
  );
};

export const FloatingTooltip = ({ content, children, className }) => {
  const [anchor, setAnchor] = useState(null);
  const show = (event) => setAnchor(event.currentTarget.getBoundingClientRect());
  const hide = () => setAnchor(null);
  return (
    <span className={cx('flex', className)} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      {children}
      {anchor && content && createPortal(
        <span
          aria-hidden="true"
          className="pointer-events-none fixed z-toast -translate-y-1/2 animate-fade-in whitespace-nowrap rounded-md border border-line-strong bg-surface-3 px-2 py-1 text-xs font-medium text-fg shadow-pop"
          style={{ top: anchor.top + anchor.height / 2, left: anchor.right + 10 }}
        >
          {content}
        </span>,
        document.body,
      )}
    </span>
  );
};

export const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');

export const Kbd = ({ children, className }) => (
  <kbd className={cx('inline-flex h-5 items-center rounded border border-line-strong bg-surface-2 px-1.5 font-sans text-[11px] font-medium text-fg-subtle', className)}>
    {children}
  </kbd>
);

export const ModKey = () => (isMac() ? '⌘' : 'Ctrl');
